import { AxiosError } from "axios";

import { apiBaseUrl } from "./config";

export type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
};

export type PaginatedResponse<T> = {
  data: T[];
  pagination: PaginationMeta;
};

export class ApiError extends Error {
  status?: number;
  cause?: unknown;
  constructor(message: string, options?: { status?: number; cause?: unknown }) {
    super(message);
    this.name = "ApiError";
    this.status = options?.status;
    this.cause = options?.cause;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function buildApiErrorMessage(error: unknown, fallback: string) {
  if (error instanceof AxiosError) {
    return error.response?.data?.error ?? error.message ?? fallback;
  }

  return error instanceof Error ? error.message : fallback;
}

export function toApiError(error: unknown, fallback: string): ApiError {
  const message = buildApiErrorMessage(error, fallback);
  const status = error instanceof AxiosError ? error.response?.status : undefined;
  return new ApiError(message, { status, cause: error });
}

/**
 * react-query retry config for endpoints whose data may not exist immediately
 * (backend background job is still computing). When such an endpoint returns
 * 404, we keep polling every ~2.5 minutes for up to 10 attempts (~25 min total).
 * Other errors fall back to the standard 3-attempt exponential backoff.
 */
export const NOT_READY_RETRY_DELAY_MS = 2.5 * 60 * 1000;
export const NOT_READY_MAX_RETRIES = 10;

export function retryOn404(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && error.status === 404) {
    return failureCount < NOT_READY_MAX_RETRIES;
  }
  return failureCount < 3;
}

export function retryOn404Delay(failureCount: number, error: unknown): number {
  if (isApiError(error) && error.status === 404) {
    return NOT_READY_RETRY_DELAY_MS;
  }
  return Math.min(1000 * 2 ** failureCount, 30_000);
}

export function normalizeTiyinPrice(value: number | null | undefined) {
  return typeof value === "number" ? value / 100 : (value ?? null);
}

export async function fetchJsonApi<T>(path: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      credentials: "include",
      next: { revalidate: 300 },
    });
  } catch (error) {
    throw new ApiError(`Request failed for ${path}`, { cause: error });
  }

  if (!response.ok) {
    let message = `Request failed for ${path} with status ${response.status}`;
    try {
      const body = (await response.clone().json()) as { error?: unknown } | null;
      if (body && typeof body.error === "string" && body.error.length > 0) {
        message = body.error;
      }
    } catch {
      // Body wasn't JSON — fall back to the default message.
    }

    throw new ApiError(message, { status: response.status });
  }

  return response.json() as Promise<T>;
}
